import axios from 'axios';

import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

import { getAuth } from '../providers/AppAuthProvider';

const base = 'xbit-relay/rebalances';

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };
}

/**
 * Wire shape of a hot-wallet rebalance. int64 fields (`srcChainId`, `dstChainId`, `*UnixMs`)
 * arrive as strings and money as decimal strings — mirrored verbatim here, converted at the
 * point of use. `attempts` is the one genuine number.
 */
export type RebalanceItem = {
  id: string;
  provider: string;
  /** Symbol leaving the source chain. */
  srcTokenSymbol: string;
  /** Symbol arriving on the destination chain — differs when the rebalance also swaps. */
  dstTokenSymbol: string;
  srcChainId: string;
  dstChainId: string;
  srcAddress: string;
  dstAddress: string;
  srcTokenAddr: string;
  dstTokenAddr: string;
  amount: string;
  minReceived: string;
  /** Free string owned by wallet-service — render unknown values as-is. */
  status: string;
  idempotencyKey: string;
  relayRequestId: string;
  srcTxHash: string;
  dstTxHash: string;
  approvedBy: string;
  /** '0' until approved. */
  approvedAtUnixMs: string;
  failureReason: string;
  attempts: number;
  createdAtUnixMs: string;
  updatedAtUnixMs: string;
};

export type RebalanceFilters = {
  page?: number;
  pageSize?: number;
  provider?: string;
  status?: string;
};

export type ListPendingRebalancesResponse = {
  items: RebalanceItem[];
  total: string;
  page: number;
  pageSize: number;
};

const toParams = (filters: Record<string, unknown>): Record<string, string> => {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return params;
};

export async function listPendingRebalances(filters: RebalanceFilters = {}) {
  const headers = await authHeaders();
  return axios.get<ListPendingRebalancesResponse>(`${baseUrl}/${base}/pending`, {
    headers,
    params: toParams(filters),
  });
}

/** Business failures come back as HTTP 200 with `success: false` — always branch on it. */
export type RebalanceActionResponse = {
  success: boolean;
  message: string;
  /** Updated row; may be absent, and never present on reject. */
  item?: RebalanceItem;
};

/**
 * All three action bodies are snake_case on purpose — they mirror the signed-withdrawal payload
 * shape. Do NOT run them through a camelCase serializer. Responses stay camelCase.
 *
 * `admin_id` is the EVM signer address, never the admin's user id or email: none of these
 * endpoints reads the JWT actor. Every signed field (`expected_amount`, `reason`,
 * `dst_tx_hash`, `timestamp_ms`) must be byte-for-byte what went into the signed message.
 */
export type ApproveRebalanceBody = {
  admin_id: string;
  /** Stale-approval guard: the exact `amount` of the row the operator is looking at. */
  expected_amount: string;
  timestamp_ms: number;
  signature: string;
};

export async function approveRebalance(id: string, body: ApproveRebalanceBody) {
  const headers = await authHeaders();
  return axios.post<RebalanceActionResponse>(`${baseUrl}/${base}/${id}/approve`, body, {
    headers,
  });
}

export type RejectRebalanceBody = {
  admin_id: string;
  reason: string;
  timestamp_ms: number;
  signature: string;
};

export async function rejectRebalance(id: string, body: RejectRebalanceBody) {
  const headers = await authHeaders();
  return axios.post<RebalanceActionResponse>(`${baseUrl}/${base}/${id}/reject`, body, {
    headers,
  });
}

export type RebalanceResolution = 'DONE' | 'FAILED';

export type ResolveRebalanceBody = {
  admin_id: string;
  resolution: RebalanceResolution;
  reason: string;
  dst_tx_hash: string;
  expected_amount: string;
  timestamp_ms: number;
  signature: string;
};

export async function resolveRebalance(id: string, body: ResolveRebalanceBody) {
  const headers = await authHeaders();
  return axios.post<RebalanceActionResponse>(`${baseUrl}/${base}/${id}/resolve`, body, {
    headers,
  });
}
